import Image from 'next/image';
import Link from 'next/link';
import BlogPost from 'components/blog/blog-post/blog-post';
import Container from 'components/container/container';

import { BLOG_ITEMS } from 'shared/data/blogs';
import {
  CUBUILD_URL,
  LIBUM_DOCS_POWERON_PRO,
  LIBUM_FEEDBACK,
  LIBUM_PORTAL,
  LIBUM_POWERON_PRO_VSCODE_URL,
  LIBUM_URL,
} from 'shared/constants/globals';
import { Sup } from 'shared/styles/components';
import { useTheme } from 'shared/styles/theme';

const BLOG = BLOG_ITEMS.find((b) => b.id === 'not-a-joke-poweron-pro')!;

export default function NotAJokePowerOnPro() {
  const title = `${BLOG.title} | Libum`;
  const description = BLOG.description;
  const keywords = BLOG.keywords.join(',');
  const url = `${LIBUM_URL}/blog/${BLOG.id}`;
  const { theme, toggleTheme } = useTheme();

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="blog"
      breadcrumbs={[
        { name: 'Blog', url: `${LIBUM_URL}/blog` },
        { name: BLOG.title, url },
      ]}
      blog={BLOG}
    >
      <div style={{ width: '100%' }}>
        <BlogPost blog={BLOG}>
          <p>
            Last summer we built the Visual Studio Code extension known as PowerOn Pro and gave the developers at{' '}
            <Link href={CUBUILD_URL} target="_blank">
              CU Build
            </Link>{' '}
            a first look. Fast forward to now, it has been{' '}
            <Link href={LIBUM_POWERON_PRO_VSCODE_URL} target="_blank">
              installed nearly 600 times
            </Link>{' '}
            by developers around the nation looking to level up their toolkit as they write software for the Jack Henry
            credit union core system.
          </p>
          <p>
            What has and will continue to make PowerOn Pro successful is the feedback our community tirelessly gives.
            Through our{' '}
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Feedback
            </Link>{' '}
            portal, they&apos;ve helped surfaced bugs, refine existing features, and even share their imagination on new
            features they&apos;d like to see.
          </p>
          <div className="divider" />
          <h3 className="anchor" id="poweron-pro-plus-release">
            <Link href="#poweron-pro-plus-release">
              PowerOn Pro<Sup>+</Sup> Release
            </Link>
          </h3>
          <p>
            In just two weeks, we will officially release PowerOn Pro<Sup>+</Sup> which aims to set the standard for
            developer tools when writing software for the Jack Henry credit union core. This means that by{' '}
            <b>April 15th</b>, anyone using a{' '}
            <Link href={`${LIBUM_DOCS_POWERON_PRO}/capabilities`} target="_blank">
              Plus
            </Link>{' '}
            feature will need to have an active subscription in order to prevent any interruptions to their development
            cycle.
          </p>
          <p>
            Between our unveiling last summer and today, we have made a list of promises for what this extension will be
            able to do. Most of these are available now, but for those that aren&apos;t — we intend to deliver on our
            promises quickly. We&apos;ll also continue to prioritize top-voted{' '}
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Feedback
            </Link>{' '}
            submissions from the community.
          </p>
          <h3 className="anchor" id="libum-portal">
            <Link href="#libum-portal">Libum Portal</Link>
          </h3>
          <p>
            Now, for those of you asking “
            <b>
              <i>why hasn&apos;t there been a PowerOn Pro update in a month</i>
            </b>
            ” let&apos;s talk about what we&apos;ve been building this quarter.
          </p>
          <p>
            Today we&apos;re announcing the initial release of <Link href={LIBUM_PORTAL}>Libum Portal</Link> — a place
            where our apps and your credit union can live together in harmony 🪴 And, to kick things off we&apos;ve
            added a tab for PowerOn Pro which gives your organization the freedom to manage your PowerOn Pro<Sup>+</Sup>{' '}
            subscription and seats completely self-serve.
          </p>
          <figure>
            <Image
              src={
                theme === 'dark'
                  ? '/images/446842fc-ab31-48f4-b1ce-179af3b24b25.png'
                  : '/images/8d89715e-7d61-4995-b499-bc77f3eab62a.png'
              }
              alt="Screenshot of Libum Portal with Subscriptions tab open"
              width={800}
              height={450}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption>The Subscriptions tab of Libum Portal</figcaption>
          </figure>
          <p>
            This is the humble beginning for Libum Portal, but we have big plans for it. Here&apos;s a sneak peek at
            what we have coming:
          </p>
          <ul>
            <li>
              <b>PowerOn Pro</b> - enforce deployment protections on particular Sym environments for your organization
            </li>
            <li>
              <b>Core Experiences</b> - we&apos;ve reimagined the UI/UX for popular areas of Symitar Quest and will be
              making these available through Libum Portal (<i>e.g. Batch Control & Print Control, to start</i>)
            </li>
            <li>
              <b>NexumAPI</b> - manage your NexumAPI as a Service (NaaS) cloud configurations through Libum Portal
            </li>
          </ul>
          <div className="divider" />
          <p>
            We&apos;re stoked to officially announce Libum Portal and excited to build feature maturity for PowerOn Pro
            <Sup>+</Sup> — as we always say, we&apos;re super sensitive to{' '}
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Feedback
            </Link>{' '}
            so if you have any, please let us know!
          </p>
        </BlogPost>
      </div>
    </Container>
  );
}
